import { useContext, useEffect, useState } from 'react'
import { PasswordResetSuccessPlaceholder as PasswordResetSuccess } from '@components'
import { NotificationContext } from "@context"
import { ADMIN_RESET_PASSWORD_MUTATION, UPDATE_EMAIL_MUTATION } from '@graphql'
import { generatePassword, getGraphQLErrorMessage, mySqlToLocal } from '@helpers'
import { useActivateAccount } from '@hooks'
import { useMutation } from '@apollo/client'
import { Button, Input } from '@heroui/react'

import { 
  ArrowLeftCircleIcon, 
  CheckCircleIcon, 
  LockOpenIcon,
  LockClosedIcon, 
  PaperAirplaneIcon, 
  ShieldExclamationIcon,
  XCircleIcon, 
} from '@heroicons/react/24/outline'

import EmailUpdateSuccess from './EmailUpdateSuccess'
import PropTypes from 'prop-types'

const AccountDetails = ({ guardian }) => {
  const [activatedAt, setActivatedAt] = useState(guardian.user.activated_at ? mySqlToLocal(guardian.user.activated_at).format("ddd Do MMM YYYY") : null)
  const [email, setEmail] = useState(guardian.user.email)
  const [isActivatingAccount, setIsActivatingAccount] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false)
  const [mode, setMode] = useState('account_details')
  const [password, setPassword] = useState('')
  const [pristineEmail, setPristineEmail] = useState(guardian.user.email)
  const { notify } = useContext(NotificationContext)
  const { activate } = useActivateAccount()

  useEffect(() => {
    setMode('account_details')
    setPristineEmail(guardian.user.email)
  }, [guardian.id, guardian.user.email])

  const [resetPassword] = useMutation(ADMIN_RESET_PASSWORD_MUTATION, {
    onCompleted: (data) => {
      setMode('password_reset_success')
    },

    onError: (error) => {
      notify({ title: "Error Resetting Password", type: "danger", content: getGraphQLErrorMessage(error, "There was a problem manually resetting the password. Please try again."), open: true })
    },

    update: (cache, { data: { adminResetPassword } }) => {
      cache.modify({
        id: cache.identify({ __typename: 'Guardian', id: guardian.id }),
        fields: {
          activated_at: () => adminResetPassword.activated_at,
        }
      })
    }
  })

  const [updateEmail] = useMutation(UPDATE_EMAIL_MUTATION, {
    onCompleted: (data) => {
      setEmail(data.updateEmail.email)
      setPristineEmail(data.updateEmail.email)
      setMode('email_update_success')
    },

    onError: (error) => {
      notify({ title: "Error Activating Account", type: "danger", content: getGraphQLErrorMessage(error, "There was a problem manually activating this account. Please try again."), open: true })
    },

    update: (cache, { data: { updateEmail } }) => {
      cache.modify({
        id: cache.identify({ __typename: 'Guardian', id: guardian.id }),
        fields: {
          email: () => updateEmail.email,
        }
      })
    }
  })

  const handleActivateAccount = () => {
    const newPassword = generatePassword(8)
    setPassword(newPassword)
    setIsActivatingAccount(true)

    activate({
      password: newPassword,
      userId: guardian.user.id,

      onSuccess: (data) => {
        setMode('account_activation_success')
        setActivatedAt(mySqlToLocal(data.adminActivateAccount.activated_at).format("ddd Do MMM YYYY"))
      },
      
      onError: (error) => {
        notify({ title: "Error Activating Account", type: "danger", content: getGraphQLErrorMessage(error, "There was a problem manually activating this account. Please try again."), open: true })
      },

      update: (cache, adminActivateAccount) => {
        cache.modify({
          id: cache.identify({ __typename: 'Guardian', id: guardian.id }),
          fields: {
            activated_at: () => adminActivateAccount.activated_at,
          }
        })
      }
    }).then(() => setIsActivatingAccount(false))
  }

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  const handleResetPassword = () => {
    setIsResettingPassword(true)
    const newPassword = generatePassword(8)
    setPassword(newPassword)

    resetPassword({
      variables: {
        input: {
          user_id: guardian.user.id,
          password: newPassword,
          password_confirmation: newPassword,
        }
      }
    }).then(() => setIsResettingPassword(false))
  }

  const handleUpdateEmail = () => {
    setIsUpdatingEmail(true)

    updateEmail({
      variables: {
        input: {
          user_id: guardian.user.id,
          email: email,
        }
      }
    }).then(() => setIsUpdatingEmail(false))
  }

  if (mode === 'account_activation_success') {
    return (
      <PasswordResetSuccess
        handleCopyToClipboard={(text) => handleCopyToClipboard(text)}
        title="Account Activated"
        message="This account has been activated. You can now login with the details below."
        password={password} 
      >
        <Button color="secondary" className="mt-6 font-semibold" size="lg" fullWidth onPress={() => setMode("account_details")}>
          <ArrowLeftCircleIcon className='w-8 h-8' /> Back to account details
        </Button>
      </PasswordResetSuccess>
    )
  }

  if (mode === 'email_update_success') {
    return (
      <EmailUpdateSuccess
        handleCopyToClipboard={(text) => handleCopyToClipboard(text)}
        handleClick={() => setMode("account_details")}
        title="Email Updated"
        email={email}
      />
    )
  }
  
  if (mode === 'password_reset_success') {
    return (
      <PasswordResetSuccess
        handleCopyToClipboard={(text) => handleCopyToClipboard(text)}
        title="Password Reset"
        message="The password for this account has been reset. You can now login with the password below."
        password={password} 
      >
        <Button color="secondary" className="mt-6 font-semibold" size="lg" fullWidth onPress={() => setMode("account_details")}>
          <ArrowLeftCircleIcon className='w-8 h-8' /> Back to account details
        </Button>
      </PasswordResetSuccess>
    )
  }

  const isEmailDirty = email !== pristineEmail

  return (
    <div>
      <div className='mb-4 border-b border-gray-300 border-dotted'>
        <div className="flex items-center gap-3 mb-1">
          <PaperAirplaneIcon className="w-8 h-8 text-indigo-800 dark:text-content2-foreground" />
          <h2 className="text-lg font-semibold">Update Email</h2>
        </div>
        
        <p className="mb-4 text-base text-content2-foreground">You'll only be able to update your email address to an email that not already in use by another user.</p>
        
        <div className="flex items-center gap-3 pb-3 mb-2">
          <Input type="email" placeholder="Email" value={email} onValueChange={setEmail} />
          <Button color="default" className='px-6' onPress={() => handleUpdateEmail()} isLoading={isUpdatingEmail} isDisabled={!isEmailDirty}>Update</Button>
        </div>
      </div>

      {activatedAt && (  
        <div className='mb-4 border-b border-gray-300 border-dotted'>
          <div className='flex items-center gap-3 mb-1'>
            <LockClosedIcon className="w-8 h-8 text-indigo-800 dark:text-content2-foreground" />
            <h2 className="text-lg font-semibold mb-q">Reset Password</h2>
          </div>

          <p className="mb-4 text-base text-content2-foreground">
            Clicking the button below will generate a secure random password. This can be altered by the parent after login if required.
          </p>
          
          <div className="flex items-center gap-3 pb-3 mb-2">
            <Button color="default" fullWidth onPress={() => handleResetPassword()} isLoading={isResettingPassword}>Reset Password</Button>
          </div>
        </div>
      )}

      {activatedAt && (
        <div className='mb-4 border-b border-gray-300 border-dotted'>
          <div className='flex items-center gap-3 mb-1'>
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
            <h2 className="text-lg font-semibold mb-q">Account Activation</h2>
          </div>

          <p className="pb-4 text-base text-content2-foreground">
            This account was activated on <span className="font-semibold">{activatedAt}</span>
          </p>
        </div>
      )}

      {!activatedAt && (
        <div className='mb-4 border-b border-gray-300 border-dotted'>
          <div className='flex items-center gap-3 mb-1'>
            <XCircleIcon className="w-8 h-8 text-orange-600" />
            <h2 className="text-lg font-semibold mb-q">Account Activation</h2>
          </div>

          <p className="pb-4 text-base text-content2-foreground">
            This account has not been activated yet. You can manually activate the account below if the parent is having issues.
          </p>

          <Button className="mb-4" color="default" fullWidth onPress={() => handleActivateAccount()} isLoading={isActivatingAccount}>
            Manually Activate
          </Button>
        </div>
      )}

      <div className='mb-4 border-b border-gray-300 border-dotted'>
        <div className='flex items-center gap-3 mb-1'>
          { guardian.user.last_active ? <LockOpenIcon className="w-8 h-8 text-green-600" /> : <ShieldExclamationIcon className="w-8 h-8 text-orange-600" />}
          <h2 className="text-lg font-semibold mb-q">Last Login</h2>
        </div>

        <p className="pb-4 text-base text-content2-foreground">
          { guardian.user.last_active ? (
            <>
              This account last logged in on <span className='font-semibold'>{mySqlToLocal(guardian.user.last_active).format("ddd Do MMM YYYY")}</span>
            </>
           ) : "This account has never logged in." }
        </p>
      </div>
    </div>
  )
}

AccountDetails.propTypes = {
  guardian: PropTypes.object.isRequired,
}

export default AccountDetails